package br.com.heyagenda.ui.components

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.heyagenda.ui.theme.AuraTheme

/**
 * Applies a pulsating gradient shimmer effect for skeleton loading states.
 * Fully reactive to light and dark theme modes.
 */
fun Modifier.auraShimmer(
    shape: Shape = RoundedCornerShape(12.dp)
): Modifier = composed {
    val transition = rememberInfiniteTransition(label = "AuraShimmerTransition")
    val translateAnimation = transition.animateFloat(
        initialValue = -300f,
        targetValue = 1200f,
        animationSpec = infiniteRepeatable(
            animation = tween(
                durationMillis = 1300,
                easing = LinearEasing
            ),
            repeatMode = RepeatMode.Restart
        ),
        label = "AuraShimmerTranslate"
    )

    val shimmerColors = if (AuraTheme.isDark) {
        listOf(
            Color(0x0CFFFFFF),
            Color(0x22FFFFFF),
            Color(0x0CFFFFFF)
        )
    } else {
        listOf(
            Color(0xFFE2E8F0),
            Color(0xFFF1F5F9),
            Color(0xFFE2E8F0)
        )
    }

    val brush = Brush.linearGradient(
        colors = shimmerColors,
        start = Offset(x = translateAnimation.value - 300f, y = translateAnimation.value - 300f),
        end = Offset(x = translateAnimation.value + 300f, y = translateAnimation.value + 300f)
    )

    this.background(brush = brush, shape = shape)
}

/**
 * Reusable skeleton placeholder box with configurable dimensions and rounded shape.
 */
@Composable
fun AuraShimmerBox(
    modifier: Modifier = Modifier,
    width: Dp? = null,
    height: Dp,
    shape: Shape = RoundedCornerShape(8.dp)
) {
    Box(
        modifier = modifier
            .then(if (width != null) Modifier.width(width) else Modifier.fillMaxWidth())
            .height(height)
            .auraShimmer(shape)
    )
}
